/**
 * Terminology Configuration
 *
 * Defines terminology mappings for FMEA and HAZID modes.
 * Allows users to switch between traditional FMEA terms and
 * HAZID/HAZOP-style terminology.
 */

export type TerminologyMode = 'fmea' | 'hazid';

export interface TerminologyConfig {
  // Mode identifier
  modeName: string;
  modeDescription: string;

  // Singular forms
  systemFunction: string;
  derivedFunction: string;
  failureMode: string;
  rootCause: string;
  effect: string;
  mitigation: string;
  rpn: string;
  requirement: string;

  // Plural forms
  systemFunctions: string;
  derivedFunctions: string;
  failureModes: string;
  rootCauses: string;
  effects: string;
  mitigations: string;
  requirements: string;

  // Full names / descriptions
  rpnFull: string;
  rpnDescription: string;

  // Consolidated risk review page (single canonical risk entry point)
  riskReview: string;
  // Standalone L×S risk register (neutral term in both modes)
  riskRegister: string;
  // The L×S risk score (R = Likelihood × Severity); distinct from RPN
  riskScore: string;

  // Abbreviations
  fmShort: string;
  rcShort: string;
  mitShort: string;

  // Action verbs (for buttons like "Add Failure Mode")
  addFailureMode: string;
  addMitigation: string;
  addRootCause: string;
  addFunction: string;

  // Contextual phrases
  noFailureModes: string;
  noMitigations: string;
  noRootCauses: string;
  noRequirements: string;
  selectFailureMode: string;
  selectMitigation: string;
  selectRequirement: string;
  addRequirement: string;
}

export const TERMINOLOGY: Record<TerminologyMode, TerminologyConfig> = {
  fmea: {
    // Mode
    modeName: 'FMEA',
    modeDescription: 'Failure Mode and Effects Analysis',

    // Singular
    systemFunction: 'System Function',
    derivedFunction: 'Derived Function',
    failureMode: 'Failure Mode',
    rootCause: 'Root Cause',
    effect: 'Effect',
    mitigation: 'Mitigation',
    rpn: 'RPN',
    requirement: 'Requirement',

    // Plural
    systemFunctions: 'System Functions',
    derivedFunctions: 'Derived Functions',
    failureModes: 'Failure Modes',
    rootCauses: 'Root Causes',
    effects: 'Effects',
    mitigations: 'Mitigations',
    requirements: 'Requirements',

    // Full names
    rpnFull: 'Risk Priority Number',
    rpnDescription: 'Severity × Occurrence × Detection',
    riskReview: 'Risk Review',
    riskRegister: 'Risk Register',
    riskScore: 'Risk Score',

    // Abbreviations
    fmShort: 'FM',
    rcShort: 'RC',
    mitShort: 'Mit',

    // Actions
    addFailureMode: 'Add Failure Mode',
    addMitigation: 'Add Mitigation',
    addRootCause: 'Add Root Cause',
    addFunction: 'Add Function',

    // Contextual
    noFailureModes: 'No failure modes found',
    noMitigations: 'No mitigations found',
    noRootCauses: 'No root causes found',
    noRequirements: 'No requirements found',
    selectFailureMode: 'Select a failure mode',
    selectMitigation: 'Select a mitigation',
    selectRequirement: 'Select a requirement',
    addRequirement: 'Add Requirement',
  },

  hazid: {
    // Mode
    modeName: 'HAZID',
    modeDescription: 'Hazard Identification Study',

    // Singular
    systemFunction: 'System Requirement',
    derivedFunction: 'Sub-system Requirement',
    failureMode: 'Hazard',
    rootCause: 'Root Cause',
    effect: 'Consequence',
    mitigation: 'Control',
    rpn: 'Risk Score',
    requirement: 'Client Requirement',

    // Plural
    systemFunctions: 'System Requirements',
    derivedFunctions: 'Sub-system Requirements',
    failureModes: 'Hazards',
    rootCauses: 'Root Causes',
    effects: 'Consequences',
    mitigations: 'Controls',
    requirements: 'Client Requirements',

    // Full names
    rpnFull: 'Risk Score',
    rpnDescription: 'Severity × Likelihood × Detectability',
    riskReview: 'Risk Review',
    riskRegister: 'Risk Register',
    riskScore: 'Risk Score',

    // Abbreviations
    fmShort: 'HZ',
    rcShort: 'RC',
    mitShort: 'Ctrl',

    // Actions
    addFailureMode: 'Add Hazard',
    addMitigation: 'Add Control',
    addRootCause: 'Add Root Cause',
    addFunction: 'Add Requirement',

    // Contextual
    noFailureModes: 'No hazards found',
    noMitigations: 'No controls found',
    noRootCauses: 'No root causes found',
    noRequirements: 'No client requirements found',
    selectFailureMode: 'Select a hazard',
    selectMitigation: 'Select a control',
    selectRequirement: 'Select a client requirement',
    addRequirement: 'Add Client Requirement',
  },
};

// Default mode
export const DEFAULT_TERMINOLOGY_MODE: TerminologyMode = 'fmea';

// localStorage key
export const TERMINOLOGY_STORAGE_KEY = 'fmea-terminology-mode';
